import {
 Button, Divider, Icon, MenuItem, Modal, TextField
} from '@nike/eds';
import { Close, Search } from '@nike/nike-design-system-icons';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import './list-selector-modal.css';

const ListSelectorModal = ({
  id,
  title,
  headerSlot,
  footerSlot,
  visible,
  setVisible,
  options,
  selectedOption,
  searchFieldProps,
  listHeight,
  displaySelectedOptionProps,
  listItemProps,
  submitButtonProps,
  cancelButtonProps,
  onItemSelect,
  doneButtonProps,
  onDismiss
}) => {
  const [localSelection, setLocalSelection] = useState(null);
  const [searchText, setSearchText] = useState('');

  const { enabled: doneBtnEnabled, onClick: doneBtnOnClick, label: doneBtnLabel } = doneButtonProps;

  useEffect(() => {
    setLocalSelection(selectedOption);
  }, [selectedOption]);

  const onListItemClick = (e, item) => {
    setLocalSelection(item);
    if (onItemSelect) onItemSelect(item);
  };

  const onClickDone = () => {
    if (localSelection) {
      doneBtnOnClick(localSelection);
    }
    setSearchText('');
    setVisible(false);
  };

  const onCancel = () => {
    cancelButtonProps.onClick();
  };

  const onSubmit = () => {
    if (localSelection) {
      submitButtonProps.onClick(localSelection);
    }
    setSearchText('');
  };

  const filteredOptions = options.filter(
    (item) => !searchText
      || item?.label?.toUpperCase()?.indexOf(searchText.toUpperCase()) !== -1
  );

  const content = (
    <div className="ncss-container">
      <div className="ncss-row list" style={{ height: listHeight }}>
        <div className="list-items-container ncss-col-sm-12 ta-sm-l">
          {filteredOptions.length ? (
          filteredOptions.map((option, idx) => {
            const isSelected = option.id === localSelection?.id;

            return (
              <div
                key={option.id}
                className="body-4"
                id={`list-item-option-${idx}`}
                style={{ cursor: 'pointer' }}
              >
                <MenuItem
                  className="p0-sm pt4-sm pb4-sm"
                  style={{ textAlign: 'left', width: '100%' }}
                  tabIndex={0}
                  onClick={(e) => onListItemClick(e, option)}
                >
                  <div
                    className="ncss-col-sm-12 pl0-sm"
                    style={{ display: 'flex' }}
                  >
                    <div className="ncss-col-sm-10">
                      <div
                        className={`d-sm-flx flx-ai-sm-c ${isSelected
                            ? 'eds-type--subtitle-1'
                            : 'eds-type--body-2 eds-color--secondary'}`}
                      >
                        <div>{listItemProps.listIcon}</div>
                        <span className="pl2-sm">{option.label}</span>
                      </div>
                    </div>
                    <div className="ncss-col-sm-2 list-row-checked">
                      {isSelected && (listItemProps?.selectionIcon || <Icon name="Check" size="m" />)}
                    </div>
                  </div>
                </MenuItem>
                { idx !== (filteredOptions.length - 1) && <Divider />}
              </div>
            );
          })
        ) : (
          <div className="ncss-row mb4-sm">
            <div className="ncss-col-sm-12">
              <div className="eds-color--secondary" id="no-stores-message"> {searchFieldProps?.noSearchResultsMessage || 'No records found'} </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  const footer = ((
    <div
      className="d-sm-flx flx-dir-sm-r flx-jc-sm-c"
      id="modal-footer"
      style={{ gap: '22px', minWidth: '400px' }}
    >
      {submitButtonProps && (
        <Button
          afterSlot={submitButtonProps?.afterSlot}
          beforeSlot={submitButtonProps?.beforeSlot}
          size="medium"
          variant="primary"
          onClick={onSubmit}
        >{submitButtonProps?.label || 'Submit'}
        </Button>
      )}
      {cancelButtonProps && (
        <Button
          afterSlot={cancelButtonProps?.afterSlot}
          beforeSlot={cancelButtonProps?.beforeSlot}
          size="medium"
          variant="primary"
          onClick={onCancel}
        >{cancelButtonProps?.label || 'Cancel'}
        </Button>
      )}
    </div>
  ));

  const header = ((
    <div
      className="ncss-container"
      id="modal-header"
      style={{ minWidth: '400px' }}
    >
      <div className="ncss-row d-sm-flx flx-ai-sm-c">
        <div className="ncss-col-sm-8 ta-sm-l">
          <h1 className="eds-type--title-4">{title}</h1>
        </div>
        {doneBtnEnabled && (
          <div className="ncss-col-sm-4 ta-sm-r">
            <Button id="list-selector-done-button" size="small" variant="ghost" onClick={onClickDone}>
              {doneBtnLabel || 'Done'}
            </Button>
          </div>
        )}
      </div>
      {displaySelectedOptionProps && (
        <div className="ncss-row ta-sm-l ml0-sm">
          <br />
          <span className="eds-type--title-6 text-color-secondary">{displaySelectedOptionProps?.label}: </span>
          <span className="eds-type--title-6">{filteredOptions.find((item) => (
            item.id === localSelection?.id
            ))?.label || displaySelectedOptionProps?.noValueSelectedText}
          </span>
        </div>
      )}
      {searchFieldProps?.enabled && (
        <div className="ncss-row mt4-sm">
          <TextField
            afterSlot={
              searchText && (
                <Close
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSearchText('')}
                />
              )
            }
            beforeSlot={<Search />}
            className="eds-type--subtitle-1"
            id="list-search-field"
            placeholder={searchFieldProps?.placeholder}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      )}
      <div className="pt4-sm">
        <Divider />
      </div>
    </div>
  ));

  return (
    <div className="list-modal" id={id}>
      <Modal
        hideFade
        footerSlot={footerSlot || footer}
        headerSlot={headerSlot || header}
        isOpen={visible}
        onDismiss={() => { if (onDismiss) onDismiss(); setVisible(false); }}
      >
        {content}
      </Modal>
    </div>
  );
};

ListSelectorModal.defaultProps = {
  cancelButtonProps: null,
  displaySelectedOptionProps: null,
  doneButtonProps: { enabled: false },
  footerSlot: null,
  headerSlot: null,
  listHeight: '30vh',
  listItemProps: {},
  onDismiss: () => {},
  onItemSelect: () => {},
  selectedOption: null,
  submitButtonProps: null,
};

ListSelectorModal.propTypes = {
  cancelButtonProps: PropTypes.shape(),
  displaySelectedOptionProps: PropTypes.shape(),
  doneButtonProps: PropTypes.shape(),
  footerSlot: PropTypes.node,
  headerSlot: PropTypes.node,
  id: PropTypes.string.isRequired,
  listHeight: PropTypes.string,
  listItemProps: PropTypes.shape(),
  onDismiss: PropTypes.func,
  onItemSelect: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  searchFieldProps: PropTypes.shape().isRequired,
  selectedOption: PropTypes.shape(),
  setVisible: PropTypes.func.isRequired,
  submitButtonProps: PropTypes.shape(),
  title: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
};

export default ListSelectorModal;
